import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth, ROLE } from '../lib/auth';

/**
 * Super Admin editor for tblSlaRule.
 *
 * Every rule the running system knows about is listed here.  For each,
 * the office can adjust the hour value and switch between
 * business-hours (respects the working calendar) and calendar-hours
 * (straight wall clock).  Rules the office no longer wants running can
 * be flipped Enabled=false — the engine skips disabled rules on
 * event write and open instances continue against their snapshots.
 */
interface SlaRule {
  ruleId: number;
  processCode: string;
  processName: string;
  slaHours: number;
  timeUnit: 'business_hours' | 'calendar_hours';
  enabled: boolean;
  startOnEventCode: string;
  completeOnEventCode: string | null;
}

export function SlaSettingsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isSuperAdmin = user?.rollId === ROLE.SuperAdmin;

  const list = useQuery({
    queryKey: ['sla-rules'],
    queryFn: () => api<SlaRule[]>('/api/sla/rules'),
    staleTime: 30_000,
  });

  const [draft, setDraft] = useState<Record<number, Partial<SlaRule>>>({});
  useEffect(() => {
    if (!list.data) return;
    // Seed drafts once when the list loads, so unsaved edits survive re-renders.
    setDraft((prev) => Object.keys(prev).length ? prev : Object.fromEntries(
      list.data.map((r) => [r.ruleId, { slaHours: r.slaHours, timeUnit: r.timeUnit, enabled: r.enabled }]),
    ));
  }, [list.data]);

  const save = useMutation({
    mutationFn: async (r: SlaRule & Partial<SlaRule>) => {
      const payload = {
        slaHours: r.slaHours,
        timeUnit: r.timeUnit,
        enabled: r.enabled,
      };
      await api(`/api/sla/rules/${r.ruleId}`, { method: 'PUT', body: JSON.stringify(payload) });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sla-rules'] }),
  });

  if (!isSuperAdmin) return <div className="p-6 text-sm text-slate-500">Super Admin only.</div>;
  if (!list.data) return <div className="p-6 text-sm text-slate-500">Loading…</div>;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-brand mb-1">SLA rules</h1>
        <p className="text-sm text-slate-500">
          Every process's deadline reads from one row below.  Business hours are 9AM–6PM Mon–Sat minus the
          lunch break defined under Calendar settings; calendar hours run straight through.
        </p>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-left text-xs uppercase text-slate-600">
            <tr>
              <th className="px-3 py-2">Process</th>
              <th className="px-3 py-2">Start event</th>
              <th className="px-3 py-2">Complete event</th>
              <th className="px-3 py-2">Hours</th>
              <th className="px-3 py-2">Clock</th>
              <th className="px-3 py-2">Enabled</th>
              <th className="px-3 py-2 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {list.data.map((r) => {
              const d = { ...r, ...(draft[r.ruleId] ?? {}) };
              const dirty =
                d.slaHours !== r.slaHours
                || d.timeUnit !== r.timeUnit
                || d.enabled !== r.enabled;
              return (
                <tr key={r.ruleId} className="border-t border-slate-100">
                  <td className="px-3 py-2">
                    <div className="font-semibold text-slate-800">{r.processName}</div>
                    <div className="text-[11px] font-mono text-slate-500">{r.processCode}</div>
                  </td>
                  <td className="px-3 py-2 text-xs font-mono text-slate-600">{r.startOnEventCode}</td>
                  <td className="px-3 py-2 text-xs font-mono text-slate-600">{r.completeOnEventCode ?? '—'}</td>
                  <td className="px-3 py-2">
                    <input
                      type="number" min={0.25} step={0.25}
                      className="input py-1 text-sm w-24"
                      value={d.slaHours}
                      onChange={(e) => setDraft((s) => ({ ...s, [r.ruleId]: { ...s[r.ruleId], slaHours: Number(e.target.value) } }))}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <select
                      className="input py-1 text-sm"
                      value={d.timeUnit}
                      onChange={(e) => setDraft((s) => ({ ...s, [r.ruleId]: { ...s[r.ruleId], timeUnit: e.target.value as SlaRule['timeUnit'] } }))}
                    >
                      <option value="business_hours">Business</option>
                      <option value="calendar_hours">Calendar</option>
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={d.enabled}
                      onChange={(e) => setDraft((s) => ({ ...s, [r.ruleId]: { ...s[r.ruleId], enabled: e.target.checked } }))}
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      className="btn-primary text-xs py-1"
                      disabled={!dirty || save.isPending}
                      onClick={() => save.mutate(d as SlaRule)}
                    >
                      {save.isPending ? 'Saving…' : dirty ? 'Save' : '—'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
